import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
} from "react";
import { useUser } from "./UserContext";
import * as api from "../services/api";
const CommentsContext = createContext();
// eslint-disable-next-line react-refresh/only-export-components
export const useComments = () => useContext(CommentsContext);
const patchTree = (rows, id, change) =>
  rows.map((row) =>
    row.id === id
      ? change(row)
      : { ...row, replies: patchTree(row.replies || [], id, change) },
  );
const merge = (rows, added) => [
  ...new Map([...rows, ...added].map((row) => [row.id, row])).values(),
];
export function CommentsProvider({ children }) {
  const { user } = useUser();
  return (
    <AccountComments key={user?.id || "guest"} user={user}>
      {children}
    </AccountComments>
  );
}
function AccountComments({ children, user }) {
  const [pages, setPages] = useState({}),
    [error, setError] = useState(null);
  const active = useRef(true),
    versions = useRef({}),
    locks = useRef(new Set());
  React.useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);
  const keyOf = (category, id) => `${category}_${id}`;
  const loadComments = useCallback(async (category, id, cursor = null) => {
    const key = keyOf(category, id),
      lock = key + ":roots";
    if (locks.current.has(lock)) return;
    locks.current.add(lock);
    const version = versions.current[key] || 0;
    setPages((prev) => ({ ...prev, [key]: { ...prev[key], loading: true } }));
    setError(null);
    try {
      const page = await api.getCommentPage(category, id, cursor);
      if (active.current)
        setPages((prev) => ({
          ...prev,
          [key]: {
            ...page,
            items:
              version !== (versions.current[key] || 0)
                ? merge(page.items, prev[key]?.items || []).sort(
                    (a, b) => b.id - a.id,
                  )
                : cursor
                  ? merge(prev[key]?.items || [], page.items)
                  : page.items,
            loading: false,
          },
        }));
    } catch (e) {
      if (active.current) setError(e.message);
    } finally {
      locks.current.delete(lock);
      if (active.current)
        setPages((prev) => ({
          ...prev,
          [key]: { ...prev[key], loading: false },
        }));
    }
  }, []);
  const loadReplies = useCallback(async (category, id, parent) => {
    const key = keyOf(category, id),
      lock = key + ":" + parent.id;
    if (locks.current.has(lock)) return;
    locks.current.add(lock);
    setError(null);
    try {
      const page = await api.getCommentPage(
        category,
        id,
        parent.replies_cursor || null,
        parent.id,
      );
      if (active.current)
        setPages((prev) => ({
          ...prev,
          [key]: {
            ...prev[key],
            items: patchTree(prev[key]?.items || [], parent.id, (row) => ({
              ...row,
              replies: merge(row.replies || [], page.items).sort(
                (a, b) => a.id - b.id,
              ),
              replies_loaded: true,
              replies_cursor: page.next_cursor,
              reply_count: page.total,
            })),
          },
        }));
    } catch (e) {
      if (active.current) setError(e.message);
    } finally {
      locks.current.delete(lock);
    }
  }, []);
  const change = useCallback((category, id, fn) => {
    if (!active.current) return;
    const key = keyOf(category, id);
    versions.current[key] = (versions.current[key] || 0) + 1;
    setPages((prev) => ({
      ...prev,
      [key]: { ...prev[key], items: fn(prev[key]?.items || []) },
    }));
  }, []);
  const addComment = useCallback(
    async (category, id, text, parent = null) => {
      if (!user) throw Error("Необходимо авторизоваться");
      const comment = await api.addComment(category, id, text, parent);
      change(category, id, (rows) =>
        parent
          ? patchTree(rows, parent, (row) => ({
              ...row,
              replies: merge(row.replies || [], [comment]),
              reply_count: Number(row.reply_count || 0) + 1,
            }))
          : merge([comment], rows),
      );
      return comment;
    },
    [user, change],
  );
  const deleteComment = useCallback(
    async (commentId, category, id) => {
      const result = await api.deleteComment(commentId);
      change(category, id, (rows) =>
        patchTree(rows, commentId, (row) => ({
          ...row,
          ...result,
          user_id: null,
          user_name: "",
          user_avatar: null,
          likes: [],
        })),
      );
    },
    [change],
  );
  const updateComment = useCallback(
    async (commentId, text, category, id) => {
      const result = await api.updateComment(commentId, text);
      change(category, id, (rows) =>
        patchTree(rows, commentId, (row) => ({ ...row, ...result })),
      );
    },
    [change],
  );
  const likeComment = useCallback(
    async (comment, category, id) => {
      const result = await api.likeComment(comment.id);
      change(category, id, (rows) =>
        patchTree(rows, comment.id, (row) => ({
          ...row,
          likes: result.liked
            ? [...new Set([...(row.likes || []), user.id])]
            : (row.likes || []).filter((id) => id !== user.id),
        })),
      );
    },
    [change, user],
  );
  const getCommentsForItem = useCallback(
    (category, id) => pages[keyOf(category, id)]?.items || [],
    [pages],
  );
  const comments = Object.fromEntries(
    Object.entries(pages).map(([key, page]) => [key, page.items || []]),
  );
  return (
    <CommentsContext.Provider
      value={{
        comments,
        pages,
        loading: Object.values(pages).some((page) => page.loading),
        error,
        loadComments,
        loadReplies,
        addComment,
        deleteComment,
        updateComment,
        likeComment,
        getCommentsForItem,
      }}
    >
      {children}
    </CommentsContext.Provider>
  );
}
export default CommentsContext;
